import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AccessToken, IDToken } from '@okta/okta-auth-js';
import {
  AuthenticationMethod,
  DossierToken,
  exchangeDossierTokenApi,
  GetDossierTokenApi,
  GetPreDeclarationTokenApi,
  OktaAccount,
  PreDeclarationToken,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private readonly apiUrl = '/api/auth';
  private readonly apiOidcUrl = '/api/auth/oidc';
  private readonly apiSamlUrl = '/api/auth/saml';
  private readonly insurerAuthURL = this.apiUrl + '/insurer';
  private readonly fitterAuthURL = this.apiUrl + '/fitter';
  private readonly accountAuthURL = this.apiUrl + '/account';
  private readonly refreshTokenUrl = this.apiUrl + '/token/refresh';
  private readonly predeclarationTokenUrl =
    this.apiUrl + '/start-pre-declaration';
  private readonly signInUrl = this.apiUrl + '/password';
  private readonly resetPasswordUrl = this.apiUrl + '/reset-password';
  private readonly superAdminSignInURL = '/api/auth';

  constructor(private readonly httpClient: HttpClient) {}

  getDossierToken(params: GetDossierTokenApi): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(this.apiUrl, params, {
      headers: { requestName: 'auth-http getDossierToken' },
    });
  }

  exchangeDossierToken(
    params: exchangeDossierTokenApi
  ): Observable<DossierToken> {
    return this.httpClient.post<DossierToken>(this.apiOidcUrl, params, {
      headers: { requestName: 'auth-http exchangeDossierToken' },
    });
  }

  forwardOidc(businessOrigin?: string) {
    let redirect_uri = window.location.origin;
    if (businessOrigin) {
      redirect_uri = encodeURIComponent(
        redirect_uri + '?businessOrigin=' + businessOrigin
      );
      window.location.replace(
        this.apiOidcUrl +
          '?businessOrigin=' +
          businessOrigin +
          '&redirect_uri=' +
          redirect_uri
      );
    } else {
      window.location.replace(
        this.apiOidcUrl + '?redirect_uri=' + redirect_uri
      );
    }
  }

  forwardSaml() {
    window.location.replace(this.apiSamlUrl);
  }

  getPreDeclarationToken(
    params: GetPreDeclarationTokenApi
  ): Observable<PreDeclarationToken> {
    return this.httpClient.post<PreDeclarationToken>(
      this.predeclarationTokenUrl,
      params,
      {
        headers: { requestName: 'auth-http getPreDeclarationToken' },
      }
    );
  }

  refreshAccessToken(refreshToken: string) {
    return this.httpClient.post<{
      accessToken: AccessToken;
      idToken: IDToken;
      refreshToken: string;
    }>(
      this.refreshTokenUrl,
      { refreshToken },
      {
        headers: {
          requestName: 'auth-http refreshAccessToken',
        },
      }
    );
  }

  doAuthenticationInsurer(
    email: string,
    authenticationMethod?: AuthenticationMethod,
    forgotPassword?: boolean
  ) {
    return this.httpClient.post(
      this.insurerAuthURL,
      { email, authenticationMethod, forgotPassword },
      {
        headers: { requestName: 'auth-http sendInsurerAuthEmail' },
      }
    );
  }

  doAuthenticationFitter(
    email: string,
    authenticationMethod?: AuthenticationMethod,
    forgotPassword?: boolean
  ) {
    return this.httpClient.post(
      this.fitterAuthURL,
      { email, authenticationMethod, forgotPassword },
      {
        headers: { requestName: 'auth-http sendFitterAuthEmail' },
      }
    );
  }

  signIn(email: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.signInUrl,
      { email, password },
      {
        headers: { requestName: 'auth-http signIn' },
      }
    );
  }

  superAdminSignIn(email: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.superAdminSignInURL,
      { email, password },
      {
        headers: { requestName: 'auth-http signIn' },
      }
    );
  }

  resetPassword(encryptedInfo: string, password: string): Observable<Object> {
    return this.httpClient.post(
      this.resetPasswordUrl,
      { encryptedInfo, password },
      {
        headers: { requestName: 'auth-http resetPassword' },
      }
    );
  }

  getOktaAccount(
    encryptedInfo: string
  ): Observable<{ oktaAccount: OktaAccount }> {
    return this.httpClient.post<{ oktaAccount: OktaAccount }>(
      this.accountAuthURL,
      { encryptedInfo },
      {
        headers: { requestName: 'auth-http resetPassword' },
      }
    );
  }
}
